/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 다이얼로그 위치 컴포넌트
 * 버전 : 1.0
 * 파일명 : DialogLocation.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 

 *================================================================
 */

import styled from 'styled-components'
import { AlertSpan, NextButton, NextButtonWrapper } from '../common/CommonStyled'
import { useDialogStore } from 'src/stores/DialogStore'
import { LoadMapDataFilter } from './LoadMapData'
import ManageRegion from '../common/ManageRegion'
import { Regions } from 'src/constant/Region'
import { useEffect, useState } from 'react'

const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 80px;
    
    padding-top: 20px;
`

const TitleWrapper = styled.span`
    font-size:20px;
    font-weight:700;
    padding: 0px 20px;
    width: 100%;
`
const RegionWrapper = styled.div`
    overflow-x:auto;
    white-space:nowrap;
    margin-left:14px; //패딩값 20px - ManageRegion 마진값 6px
    width: calc(100vw - 14px);
`
const RegionSelectorWrapper = styled.div`
    display: flex;
    border-top: 1px solid #DDD;;
`
const RegionKeyWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 38.7vw;
    border-right: 1px solid #DDD;;
    overflow-y: auto;
    height: calc(100vh - 72px - 199.4px);
    white-space: nowrap;
`
const RegionKeyContainer = styled.div<{$isSelected:boolean, $isIncluded:boolean}>`
    padding: 15px 20px;
    font-size: 16px;
    font-weight: 600;
    color: ${({ $isSelected, $isIncluded }) => ($isSelected ? '#229596' : $isIncluded ? '#229596' : '#A1A1A1')};
    background-color: ${({ $isSelected }) => ($isSelected ? '#fff' : '#F5F5F5')};
`
const RegionValueWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: #fff;
    overflow-y: auto;
    height: calc(100vh - 72px - 199.4px);
`
const RegionValueContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;

    padding: 15px 20px;
    font-size: 16px;
    font-weight: 600;
    color: #A1A1A1;
`
const CustomCheckBox = styled.input.attrs({ type: 'checkbox' })`
    width: 16px;
    height: 16px;
    accent-color: #229596;
    outline: none;
    box-shadow: none;
    &:active {
        accent-color: #229596; // active 상태에서도 동일하게
    }
`
const DialogLocation = () => {
    const contents = Regions
    const locations = useDialogStore((state)=>state.locations)
    const temp_locations = useDialogStore((state)=>state.temp_locations)
    const setRegions = useDialogStore((state)=>state.setRegions)
    const setTempLocations = useDialogStore((state)=>state.setTempLocations)
    const setField = useDialogStore((state)=>state.setField)
    const isValid = !temp_locations || Object.keys(temp_locations).length === 0
    const contentKey = Object.keys(contents)
    const [selectedKey,setSelectedKey] = useState(contentKey[0])

    useEffect(()=>{
        if(temp_locations !== locations){
            setField('temp_locations',locations)
        }
    },[])

    const handleSubmit = async () => {
        await setRegions()
        await setField('locations',temp_locations)
        LoadMapDataFilter();
        setField('isOpen',false)
    }
    
    // Locations 값을 다시 원상태로 되돌림
    function getOriginalWidelocList(shortNames: string[]): string[] {
        return shortNames.map((wideKey) => {
            return (
            Object.keys(Regions).find((fullName) => {
                if (fullName.endsWith('남도') || fullName.endsWith('북도')) {
                return wideKey === fullName[0] + fullName[2]; // 예: 충남 = 충청남도
                } else {
                return wideKey === fullName.slice(0, 2); // 예: 서울 = 서울특별시
                }
            }) ?? '' // 못 찾으면 빈 값 반환
            );
        });
    }
    const parsedLocations = getOriginalWidelocList(Object.keys(temp_locations))
    function parseWideKey(wideloc: string): string {
        if (wideloc.endsWith('남도') || wideloc.endsWith('북도')) {
                return wideloc[0] + wideloc[2]; // 예: 전라남도 → 전남
        } else {
                return wideloc.slice(0, 2); // 예: 서울특별시 → 서울
            }
        }
    const onClickSetRegions = (wide_loc: string, city_loc:string)=>{
        const newLocations = { ...temp_locations };
        const existing = newLocations[wide_loc] ?? [];

        if (city_loc === "전체") {
            if (existing.includes("전체")) {
            // 이미 "전체"가 선택된 경우 → 제거
            delete newLocations[wide_loc];
            } else {
            // "전체" 선택 시 해당 도의 값을 "전체"만 남김
            newLocations[wide_loc] = ["전체"];
            }
        } else {
            // "전체"가 있으면 제거
            const filtered = existing.filter((item) => item !== "전체");

            if (existing.includes(city_loc)) {
            // 이미 선택된 항목이면 제거
            const next = filtered.filter((item) => item !== city_loc);
            if (next.length === 0) {
                delete newLocations[wide_loc]; // 선택 없으면 아예 삭제
            } else {
                newLocations[wide_loc] = next;
            }
            } else {
            // 선택 안된 항목이면 추가
            newLocations[wide_loc] = [...filtered, city_loc];
            }
        }
        setTempLocations(newLocations);
    }
  return (
    <PageWrapper>
        <TitleWrapper>
            지역
        </TitleWrapper>
        {!isValid ? 
            <RegionWrapper className='hide-scrollbar'>
                <ManageRegion user_area={temp_locations} isModify={false} isTemp={true}/>
            </RegionWrapper>
        :
            <RegionWrapper style={{marginLeft:'20px', width:'calc(100vw - 20px)'}}>
                <AlertSpan $isValid={false} style={{height:'41px', padding:'0px', display:'flex', alignItems:'center'}}>담당 지역을 1개 이상 설정해주세요</AlertSpan>
            </RegionWrapper>
        }
        <RegionSelectorWrapper>
            <RegionKeyWrapper>
                {contentKey.map((key) => {
                    const isIncluded = parsedLocations.includes(key);
                    return(
                        <RegionKeyContainer onClick={()=>setSelectedKey(key)} $isSelected={key === selectedKey} $isIncluded={isIncluded}>
                            {key} {isIncluded && '✓'}
                        </RegionKeyContainer>
                    )
                })}
            </RegionKeyWrapper>
            <RegionValueWrapper>
                {contents[selectedKey].map((value)=>{
                    const parsedKey = parseWideKey(selectedKey) ?? ''
                    const isIncluded = temp_locations[parsedKey]?.includes(value) ?? false
                    return(
                        <RegionValueContainer>
                            <CustomCheckBox checked={isIncluded} onClick={()=>onClickSetRegions(parsedKey,value)}/> {value}
                        </RegionValueContainer>
                    )
                })}
            </RegionValueWrapper>
        </RegionSelectorWrapper>
        <NextButtonWrapper $isBorder={false}>
            <NextButton style={{background:'#F5F5F5', color:'#7F7F89', minWidth:'90px', width:'90px', boxShadow:'none', border:'1px solid #00001480'}} onClick={()=>setField('temp_locations',locations)}>초기화</NextButton>
            <NextButton onClick={handleSubmit} disabled={isValid} style={{margin:'5px',padding:'10px', height:'46px', boxShadow:'none'}}>적용</NextButton>
        </NextButtonWrapper>
    </PageWrapper>
)
}

export default DialogLocation